import { AlertCircle, CheckCircle, InfoCircle } from '@untitledui/icons';
import type { ReactNode } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { CloseButton } from '@/lib/base/buttons/close-button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { cx } from '@/utils/cx';

const iconMap = {
  default: InfoCircle,
  brand: InfoCircle,
  gray: InfoCircle,
  error: AlertCircle,
  warning: AlertCircle,
  success: CheckCircle,
};

const fullWidthColorStyles = {
  default: 'bg-secondary_subtle border-primary',
  brand: 'bg-brand-50 dark:bg-brand-950 border-brand-200 dark:border-brand-800',
  gray: 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700',
  error: 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800',
  warning:
    'bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800',
  success:
    'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800',
};

const fullWidthTextStyles = {
  default: { title: 'text-secondary', description: 'text-tertiary' },
  brand: {
    title: 'text-brand-900 dark:text-brand-100',
    description: 'text-brand-700 dark:text-brand-300',
  },
  gray: {
    title: 'text-gray-900 dark:text-gray-100',
    description: 'text-gray-700 dark:text-gray-300',
  },
  error: {
    title: 'text-red-900 dark:text-red-100',
    description: 'text-red-700 dark:text-red-300',
  },
  warning: {
    title: 'text-amber-900 dark:text-amber-100',
    description: 'text-amber-700 dark:text-amber-300',
  },
  success: {
    title: 'text-green-900 dark:text-green-100',
    description: 'text-green-700 dark:text-green-300',
  },
};

interface AlertFloatingProps {
  /**
   * The title of the alert.
   */
  title: string;
  /**
   * The description of the alert.
   */
  description?: ReactNode;
  /**
   * The label for the confirm button.
   */
  confirmLabel?: string;
  /**
   * The label for the dismiss button.
   * @default "Dismiss"
   */
  dismissLabel?: string;
  /**
   * The color of the alert.
   * @default "default"
   */
  color?: 'default' | 'brand' | 'gray' | 'error' | 'warning' | 'success';
  /**
   * The function to call when the dismiss button is clicked.
   */
  onClose?: () => void;
  /**
   * The function to call when the confirm button is clicked.
   */
  onConfirm?: () => void;
  /**
   * Whether the confirm action is in flight. Shows a spinner in the confirm
   * button so an alert whose CTA triggers slow work (an agent run, a network
   * request) can signal progress instead of looking inert and inviting a
   * second click.
   * @default false
   */
  isConfirmLoading?: boolean;
  /**
   * Whether the confirm button is disabled.
   * @default false
   */
  isConfirmDisabled?: boolean;
}

export const AlertFloating = ({
  title,
  description,
  confirmLabel,
  onClose,
  onConfirm,
  color = 'default',
  dismissLabel = 'Dismiss',
  isConfirmLoading = false,
  isConfirmDisabled = false,
}: AlertFloatingProps) => {
  const isTitleOnly = !description && !onConfirm && !onClose;
  return (
    <div
      className={cx(
        'relative flex flex-col gap-4 rounded-xl border border-primary bg-primary_alt p-4 shadow-xs md:flex-row',
        isTitleOnly && 'md:items-center'
      )}
    >
      <FeaturedIcon
        icon={iconMap[color]}
        color={color === 'default' ? 'gray' : color}
        theme={color === 'default' ? 'modern' : 'light-outline'}
        size='md'
      />
      <div className='flex flex-1 flex-col gap-3 md:w-0'>
        <div className='flex flex-col gap-1 overflow-auto'>
          <p className='pr-8 text-sm font-semibold text-secondary'>{title}</p>
          {description && (
            <p className='text-sm text-tertiary'>{description}</p>
          )}
        </div>
        {(onConfirm || onClose) && (
          <div className='flex gap-3'>
            {onClose && (
              <Button onClick={onClose} size='sm' color='link-gray'>
                {dismissLabel}
              </Button>
            )}
            {onConfirm && confirmLabel && (
              <Button
                onClick={onConfirm}
                size='sm'
                color='link-color'
                isLoading={isConfirmLoading}
                isDisabled={isConfirmDisabled}
              >
                {confirmLabel}
              </Button>
            )}
          </div>
        )}
      </div>
      {onClose && (
        <CloseButton
          onClick={onClose}
          size='sm'
          label={dismissLabel}
          className='absolute top-2 right-2'
        />
      )}
    </div>
  );
};

interface AlertFullWidthProps {
  /**
   * The title of the alert.
   */
  title: string;
  /**
   * The description of the alert.
   */
  description: ReactNode;
  /**
   * The label for the confirm button.
   */
  confirmLabel?: string;
  /**
   * The label for the dismiss button.
   * @default "Dismiss"
   */
  dismissLabel?: string;
  /**
   * The type of the action buttons.
   * @default "button"
   */
  actionType?: 'button' | 'link';
  /**
   * The color of the alert.
   * @default "default"
   */
  color?: 'default' | 'brand' | 'gray' | 'error' | 'warning' | 'success';
  /**
   * The function to call when the dismiss button is clicked.
   */
  onClose?: () => void;
  /**
   * The function to call when the confirm button is clicked.
   */
  onConfirm?: () => void;
  /**
   * When true, keeps the title stacked above the description at all breakpoints
   * instead of flipping to a side-by-side row at the `lg` breakpoint.
   * @default false
   */
  stackContent?: boolean;
}

export const AlertFullWidth = ({
  title,
  description,
  confirmLabel,
  onClose,
  onConfirm,
  color = 'default',
  actionType = 'button',
  dismissLabel = 'Dismiss',
  stackContent = false,
}: AlertFullWidthProps) => {
  const colorStyle = fullWidthColorStyles[color];
  const textStyle = fullWidthTextStyles[color];

  return (
    <div
      className={cx('relative border-t md:border-t-0 md:border-b', colorStyle)}
    >
      <div className='mx-auto flex max-w-container flex-col gap-4 p-4 md:flex-row md:items-center md:gap-3 md:px-8 md:py-3'>
        <div className='flex flex-1 flex-col gap-4 md:w-0 md:flex-row md:items-center'>
          <FeaturedIcon
            className='hidden md:flex'
            icon={iconMap[color]}
            color={color === 'default' ? 'gray' : color}
            theme={color === 'default' ? 'modern' : 'light-outline'}
            size='md'
          />
          <div
            className={cx(
              'flex min-w-0 flex-col gap-0.5',
              !stackContent && 'lg:flex-row lg:gap-1.5'
            )}
          >
            <p
              className={cx(
                'shrink-0 pr-8 text-sm font-semibold md:pr-0',
                textStyle.title
              )}
            >
              {title}
            </p>
            {description && (
              <p className={cx('text-sm', textStyle.description)}>
                {description}
              </p>
            )}
          </div>
        </div>
        {(onConfirm || onClose) && (
          <div className='flex gap-2'>
            <div
              className={cx(
                'flex w-full gap-3',
                actionType === 'button'
                  ? 'flex-col-reverse md:flex-row'
                  : 'flex-row'
              )}
            >
              {onClose && (
                <Button
                  onClick={onClose}
                  color={actionType === 'button' ? 'secondary' : 'link-gray'}
                  size={actionType === 'button' ? 'md' : 'sm'}
                >
                  {dismissLabel}
                </Button>
              )}
              {onConfirm && confirmLabel && (
                <Button
                  onClick={onConfirm}
                  color={actionType === 'button' ? 'primary' : 'link-color'}
                  size={actionType === 'button' ? 'md' : 'sm'}
                >
                  {confirmLabel}
                </Button>
              )}
            </div>
            {onClose && (
              <CloseButton
                onClick={onClose}
                size='sm'
                label={dismissLabel}
                className='absolute top-2 right-2 shrink-0 md:static'
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
